import { AfterViewInit, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthApiService } from '../../../../_services/auth-api-service/auth-api.service';
import { ClearAppState, NavigateToDashboardFromLogin } from '../../../../store/app/app.actions';
import { SetTokens } from '../../../../store/auth/auth.actions';
import { Method } from '@bo/ng-api';
import { Logout, TokenResponse } from '@bo/ng-auth';
import { BoFormsModule, InputType } from '@bo/ng-forms';
import { FieldComponent } from '../../forms/form-field/form-field.component';
import { PasswordFieldComponent } from '../../forms/password-field/password-field.component';
import { SubmitComponent } from '../../forms/submit/submit.component';
import { LoaderComponent } from '../../loader/loader.component';
import { AppFormComponent } from '../../../classes/app-form';
import { Router } from '@angular/router';
import { SetSubscriptionStateFromUserLogin } from '../../../../store/subscription/subscription.actions';
import { AppSelectors } from '../../../../store/app/app.selectors';

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    BoFormsModule,
    FieldComponent,
    PasswordFieldComponent,
    SubmitComponent,
    LoaderComponent,
  ],
  templateUrl: './login-modal.component.html',
})
export class LoginModalPaysComponent extends AppFormComponent<TokenResponse> implements AfterViewInit {
  readonly store = inject(Store);
  readonly authService = inject(AuthApiService);
  readonly ngbActiveModal = inject(NgbActiveModal);
  readonly router = inject(Router);

  protected fields = ['username', 'password'];
  protected readonly InputType = InputType;
  readonly userEmail$ = this.store.select(AppSelectors.userEmail);

  ngAfterViewInit(): void {
    // Focus password field after modal is rendered
    setTimeout(() => {
      const modalElement = document.querySelector('.modal');
      const passwordInput = modalElement?.querySelector('input[type="password"]') as HTMLInputElement;
      if (passwordInput) {
        passwordInput.focus();
      }
    }, 150);
  }

  createForm(): void {
    this.buildFromOptions(this.authService.getOptions(this.authService.URL_LOGIN, Method.POST)).subscribe({
      next: () => {
        this.subscriptions.add(
          this.userEmail$.subscribe((email) => {
            if (email) {
              this.form.get('username')?.setValue(email);
            }
          }),
        );
      },
    });

    // Close the modal if user lands on the login page.
    const currentPath = globalThis.location.pathname;
    if (currentPath.includes('/login') || this.router.url.includes('/login')) {
      this.ngbActiveModal.close();
    }
  }

  submitData(): Observable<TokenResponse> {
    return this.authService.login(this.form.value.username, this.form.value.password);
  }

  handleSubmitSuccess(data: TokenResponse): void {
    if (data.access != null) {
      this.store.dispatch(new SetTokens(data));
      this.store.dispatch(new SetSubscriptionStateFromUserLogin());

      this.store.dispatch(new NavigateToDashboardFromLogin()).subscribe({
        next: () => {
          this.ngbActiveModal.close(true);
        },
        error: () => {
          // Still close modal even on error
          this.ngbActiveModal.close(true);
        },
      });
    }
  }

  protected override handleSubmitError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      const message = error.error?.detail || 'Invalid username or password.';
      this.form.setErrors({ server: message });
      return;
    }
    super.handleSubmitError(error);
  }

  public onCancel(): void {
    this.ngbActiveModal.dismiss();
    this.store.dispatch(new ClearAppState());
    this.store.dispatch(new Logout());
  }
}
